import { useState } from 'react'
import { createRoute } from '@tanstack/react-router'
import { Tag } from 'lucide-react'
import rootRoute from './rootRoute'
import type { InitiativeTag } from '@/lib/initiatives'
import { FilterPill, PageHeader, Row, spacing } from '@/components/ui'
import EntityCard from '@/components/EntityCard'
import { INITIATIVES, initiativeToneOf } from '@/lib/initiatives'

const ALL_TAGS = [...new Set(INITIATIVES.flatMap((i) => i.tags))].sort((a, b) =>
  a.localeCompare(b, 'fr'),
)

function TagDot({ color }: { color: string }) {
  return (
    <span
      style={{
        width: spacing.sm,
        height: spacing.sm,
        borderRadius: 'var(--radius-pill)',
        background: color,
        flexShrink: 0,
      }}
    />
  )
}

function InitiativesPage() {
  const [filterTag, setFilterTag] = useState<InitiativeTag | null>(null)

  const initiatives = INITIATIVES.filter(
    (i) => filterTag === null || i.tags.includes(filterTag),
  )

  return (
    <div
      style={{
        maxWidth: 'var(--container-max)',
        margin: '0 auto',
        padding: 'var(--space-8) var(--gutter) var(--space-20)',
      }}
    >
      <PageHeader
        overline="Au-delà d'Oumma"
        title="Autres initiatives musulmanes"
        subtitle="Des chaînes, des podcasts, des associations et des projets portés par la communauté, au-delà de cette application. Une sélection à découvrir et à soutenir."
        style={{ marginBottom: spacing.xl }}
      />

      <Row wrap gap="sm" style={{ marginBottom: spacing.lg }}>
        <Row gap="xs" style={{ color: 'var(--ink-3)', fontSize: 14 }}>
          <Tag size={16} />
          Tags
        </Row>
        <FilterPill
          active={filterTag === null}
          onClick={() => setFilterTag(null)}
        >
          Toutes ({INITIATIVES.length})
        </FilterPill>
        {ALL_TAGS.map((tag) => {
          const active = filterTag === tag
          const tone = initiativeToneOf(tag)
          return (
            <FilterPill
              key={tag}
              active={active}
              activeColor={tone.color}
              onClick={() => setFilterTag(active ? null : tag)}
            >
              <TagDot color={active ? 'var(--paper-0)' : tone.color} />
              {tag}
            </FilterPill>
          )
        })}
      </Row>

      <p style={{ color: 'var(--ink-2)', margin: `0 0 ${spacing.xl}` }}>
        <strong style={{ color: 'var(--ink-0)' }}>{initiatives.length}</strong>{' '}
        initiative{initiatives.length > 1 ? 's' : ''} trouvée
        {initiatives.length > 1 ? 's' : ''}
      </p>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
          gap: 'var(--space-5)',
        }}
      >
        {initiatives.map((i, idx) => (
          <EntityCard
            key={i.name}
            entity={{
              id: idx,
              name: i.name,
              description: i.description,
              kind_id: 0,
              tags: i.tags,
              image_uri: i.image,
            }}
            kinds={[]}
            href={i.link}
            clampDescription={false}
            tagToneOf={initiativeToneOf}
          />
        ))}
      </div>
    </div>
  )
}

export const initiativesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/initiatives',
}).lazy(() => import('./InitiativesPage.lazy').then((m) => m.Route))

export default InitiativesPage
